import { blade, brush, fan, inkStroke, peak, scroll, splats, sprite, star, target, eye } from '../kit';
import { C, mix, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กระบี่แท่งหมึกสน (หอหมึกสน, soft): the scholar's brush and sword. Ink is drawn on paper so
 * it reads on the dark tile.
 */

const INK = '#1e1a24';
const PAPER = C.paper;
const WASH = '#8ab8d0';

/** A sheet of paper with a soft shadowed edge. */
function sheet(p: Pix, x: number, y: number, w: number, h: number): void {
  p.rect(x, y, w, h, PAPER);
  p.rect(x, y + h - 1, w, 1, '#d8d0c0');
}

registerIcons('skill', {
  // One sweeping stroke of the brush across a sheet.
  'jz-stroke': (p) => {
    sheet(p, 3, 12, 22, 14);
    inkStroke(
      p,
      [
        [5, 22],
        [11, 17],
        [18, 19],
        [23, 15],
      ],
      5,
      INK,
    );
    brush(p, 29, 2, 21, 13, INK);
  },
  // A five-word poem: three columns of verse, and the blade that writes them.
  'jz-poem': (p) => {
    scroll(p, 6, 4, 14, 24);
    for (const x of [9, 13, 17]) for (let y = 8; y < 25; y += 3) p.rect(x, y, 2, 2, INK);
    blade(p, 20, 27, 29, 3, { w: 2.2, col: C.steel });
    p.rect(19, 26, 4, 2, C.gold);
  },
  // Ink flung in every direction.
  'jz-ink': (p) => {
    p.ell(16, 16, 12, 12, PAPER);
    splats(
      p,
      [
        [16, 16, 5],
        [9, 9, 2],
        [24, 10, 2.5],
        [25, 22, 2],
        [8, 23, 2.5],
        [16, 6, 1.5],
        [6, 16, 1.5],
        [27, 16, 1.5],
      ],
      INK,
    );
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4;
      p.line(16 + Math.cos(a) * 5, 16 + Math.sin(a) * 5, 16 + Math.cos(a) * 9, 16 + Math.sin(a) * 9, INK);
    }
  },
  // Two fingers strike an acupoint, which seals.
  'jz-seal': (p) => {
    sprite(
      p,
      2,
      13,
      [
        '.......sssssssss',
        '.....sssssssssss',
        '..sssssss.......',
        'sssssssss.......',
        'ssssssss........',
        '.sssss..........',
      ],
      { s: C.skin },
    );
    target(p, 24, 14, 4, '#ffe07a');
    for (let k = 0; k < 4; k++) {
      const a = (k * Math.PI) / 2 + Math.PI / 4;
      p.px(24 + Math.cos(a) * 7, 14 + Math.sin(a) * 7, C.white);
    }
  },
  // A folding fan opened as a screen, a mountain painted on it.
  'jz-ward': (p) => {
    fan(p, 17, 26, 15, PAPER, C.wood);
    peak(p, 13, 20, 10, 8, WASH, null);
    peak(p, 20, 20, 12, 10, mix(WASH, C.ink, 0.3), PAPER);
    p.ell(17, 26, 1.5, 1.5, C.gold);
  },
  // Insight: one ink circle drawn in a breath, the eye open in it.
  'jz-focus': (p) => {
    p.ell(17, 16, 11, 11, PAPER);
    for (let a = -1.2; a < 4.6; a += 0.02) {
      const w = a < 0 || a > 4 ? 1 : 2;
      for (let q = 0; q < w; q++) p.px(17 + Math.cos(a) * (10 - q), 16 + Math.sin(a) * (10 - q), INK);
    }
    eye(p, 17, 16, 10, '#3a8ac8', PAPER);
    star(p, 26, 6, 2, C.white);
  },
  // One great written word that breaks a line of men: a bold mark stamped through a sheet.
  'jz-break': (p) => {
    sheet(p, 3, 3, 18, 18);
    inkStroke(
      p,
      [
        [5, 8],
        [19, 7],
      ],
      3,
      INK,
    );
    inkStroke(
      p,
      [
        [12, 4],
        [11, 19],
      ],
      3,
      INK,
    );
    inkStroke(
      p,
      [
        [5, 15],
        [18, 13],
      ],
      3,
      INK,
    );
    p.path(
      [
        [21, 3],
        [18, 9],
        [21, 12],
        [17, 19],
      ],
      C.seal,
      1,
    );
    brush(p, 29, 12, 22, 20, INK);
  },
  // A great scroll unrolled, its ink rolling out as waves.
  'jz-scroll': (p) => {
    p.rect(4, 6, 24, 15, PAPER);
    p.rect(3, 4, 3, 19, C.wood);
    p.ell(27, 13, 2.5, 9, '#e8dcc4');
    for (let k = 0; k < 3; k++)
      for (let x = 8; x < 25; x++) p.px(x, 10 + k * 4 + Math.round(Math.sin(x / 1.8 + k) * 1.2), INK);
    for (let x = 4; x < 29; x++) p.rect(x, 25 + Math.round(Math.sin(x / 2.2)), 1, 2, WASH);
  },
});
